import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, OverlayTrigger, Row, Spinner, Table, Tooltip } from 'react-bootstrap';
import { Campaign } from '../../../model/campaign/Campaign';
import { IUser } from '../../../model/user/IUser';
import { ReportsService } from '../ReportsService';
import { IReportItem } from '../../../model/report/Report-item';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { FiInfo } from 'react-icons/fi';
import { formatDate, getPlatformValue } from '../../../utils/Functions';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { CampaignsService } from '../../campaigns/CampaignsService';
import { UserService } from '../../user/UserService';
import { NavLink } from 'react-router-dom';
import { debounce } from 'lodash';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ITableColumn } from '../../../shared/components/TableSkeleton';
import { downloadFileRequest } from '../../../api/api.service';
import { REPORTING_LIST_DOWNLOAD_URL } from '../../../utils/Endpoints';
import AutocompleteInput from '../../../shared/components/AutocompleteInput';
import { ErrorToast } from '../../../utils/toasters';
import { Offer } from '../../../model/offer/Offer';
import SingleSelectDropdown, { toSelectValue } from '../../../shared/components/SingleSelectDropdown';

interface IReportingList {
    isRejected?: boolean;
    sortBy?: string;
    sortType?: 'DESC' | 'ASC';
}

const getReportColumns = (isRejected?: boolean): ITableColumn[] => [
    { label: 'Report ID' },
    { label: 'Campaign ID' },
    { label: 'Reported at' },
    { label: 'Updated At' },
    { label: 'Influencer' },
    { label: 'Email' },
    { label: 'Offer ID' },
    { label: 'Platform' },
    ...(isRejected ? [{ label: 'Rejection date' }, { label: 'Rejection reason' }] : [{ label: 'is Verified?' }]),
    { label: 'Action', className: 'text-end' },
];

export default function ReportsList({ isRejected, sortBy, sortType }: IReportingList) {
    const [dataLoading, setDataLoading] = useState<boolean>(true);
    const columns = useMemo(() => getReportColumns(isRejected), [isRejected]);
    const [pagination, setPagination] = useState({
        page: 1,
        perPage: 15,
        totalPages: 1,
        isVerified: false,
        sortBy: sortBy ?? 'id',
        sortType: sortType ?? 'DESC',
    });
    const [reportList, setReportList] = useState<IReportItem[]>([]);
    const [triggerSearch, setTriggerSearch] = useState<boolean>(false);

    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [campaignId, setCampaignId] = useState('');

    const [users, setUsers] = useState<IUser[]>([]);
    const [userId, setUserId] = useState('');
    const [campaignsParams, setCampaignsParams] = useState('');
    const [usersParams, setUsersParams] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const getCampaigns = (title: string) => {
        CampaignsService.getCampaigns({ perPage: 500, title, isLongTerm: false })
            .then(data => setCampaigns(data.data))
            .catch(ErrorToast);
    };

    const getUsers = (displayName: string) => {
        UserService.getUsers({ perPage: 500, userType: 'influencer', displayName })
            .then(data => setUsers(data.data))
            .catch(ErrorToast);
    };

    const campaignsDebounce = useMemo(() => debounce(getCampaigns, 300), []);
    const usersDebounce = useMemo(() => debounce(getUsers, 300), []);

    useEffect(() => {
        getCampaigns(campaignsParams);
        getUsers(usersParams);
    }, []);

    useEffect(() => {
        setDataLoading(true);
        ReportsService.getReports({ ...pagination, campaignId, userId, isRejected })
            .then(data => {
                setReportList(data.data);
                setPagination(prev => ({ ...prev, totalPages: Math.ceil(data.count / pagination.perPage) }));
            })
            .catch(ErrorToast)
            .finally(() => setDataLoading(false));
    }, [pagination.page, triggerSearch, userId, campaignId, isRejected]);

    useEffect(() => {
        if (pagination.page > pagination.totalPages && pagination.totalPages > 0) {
            setPagination(prev => ({ ...prev, page: 1 }));
        }
    }, [pagination.totalPages]);

    const handlePages = (updatePage: number) => {
        setPagination(prev => ({ ...prev, page: updatePage }));
    };

    const handleChange = (evt: any) => {
        const { name, value } = evt.target;
        setPagination(prev => ({ ...prev, [name]: value }));
    };

    function getPreferredUser(offer: Offer): { id: number; email: string } {
        const assigned = offer.user.assignedAgencyUser?.[0];
        const createdBy = offer.user.agencyCompany?.createdBy;
        const user = offer.user;

        return assigned || createdBy || user;
    }

    return (
        <div>
            <Card className="mb-5 p-4 mt-3" onKeyDown={e => e.key === 'Enter' && setTriggerSearch(prev => !prev)}>
                <Row style={{ rowGap: '18px' }}>
                    <Form.Group as={Col} className="col-4 mb-0 autocomplete-input zindex-5 position-relative">
                        <AutocompleteInput
                            items={campaigns}
                            placeholder="Campaign title"
                            value={campaignsParams}
                            renderItems={['title']}
                            setFilter={value => {
                                campaignsDebounce(value);
                                setCampaignsParams(value);
                                if (!value) setCampaignId('');
                            }}
                            onSelect={campaign => setCampaignId(campaign?.id)}
                        />
                    </Form.Group>
                    <Form.Group as={Col} className="mb-0 autocomplete-input zindex-5 position-relative">
                        <AutocompleteInput
                            items={users}
                            placeholder="Influencer name"
                            value={usersParams}
                            renderItems={['displayName']}
                            setFilter={value => {
                                usersDebounce(value);
                                setUsersParams(value);
                                if (!value) setUserId('');
                            }}
                            onSelect={user => setUserId(user?.id)}
                        />
                    </Form.Group>
                    <Col md={2} xs={6}>
                        <SingleSelectDropdown
                            placeholder="is Additional?"
                            options={[
                                { value: 'true', label: 'Additional' },
                                { value: 'false', label: 'Not Additional' },
                            ]}
                            selected={toSelectValue((pagination as any).isAdditional, 'undefined')}
                            onChange={value =>
                                handleChange({ target: { name: 'isAdditional', value: value || 'undefined' } })
                            }
                        />
                    </Col>
                    <Col md={2} xs={6}>
                        <SingleSelectDropdown
                            placeholder="is Verified?"
                            options={[
                                { value: 'true', label: 'Verified' },
                                { value: 'false', label: 'Not Verified' },
                            ]}
                            selected={toSelectValue((pagination as any).isVerified, 'undefined')}
                            onChange={value =>
                                handleChange({ target: { name: 'isVerified', value: value || 'undefined' } })
                            }
                        />
                    </Col>
                </Row>
                <div className="d-flex gap-4">
                    <Button className="mt-4" disabled={isLoading} onClick={() => setTriggerSearch(prev => !prev)}>
                        Search
                    </Button>
                    <Button
                        className="mt-4"
                        variant="outline-primary"
                        onClick={() =>
                            downloadFileRequest(
                                REPORTING_LIST_DOWNLOAD_URL,
                                'export-report',
                                null,
                                pagination,
                                setIsLoading
                            )
                        }
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                Generating excel...{' '}
                                <Spinner animation="border" size="sm" role="status" className="ml-1" />
                            </>
                        ) : (
                            'Export current'
                        )}
                    </Button>
                </div>
            </Card>

            <TableLayout
                pagination={pagination}
                handlePages={handlePages}
                listLength={reportList.length}
                isLoading={dataLoading}
                dataName={'reports'}
                skeletonColumns={columns}
                skeletonRows={pagination.perPage}
            >
                <div className="table-responsive">
                    <Table hover style={{ whiteSpace: 'nowrap' }}>
                        <thead>
                            <tr>
                                {columns.map(column => (
                                    <th key={column.label} className={column.className}>
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {reportList.map(item => (
                                <tr key={item.id}>
                                    <td>{item?.id}</td>
                                    <td>
                                        {item?.offer?.campaign?.id}
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip id="campaign-tooltip">{item?.offer?.campaign?.title}</Tooltip>
                                            }
                                        >
                                            <span className="ml-1">
                                                <FiInfo size={14} className="cursor-pointer" />
                                            </span>
                                        </OverlayTrigger>
                                    </td>
                                    <td>{formatDate(item?.createdAt)}</td>
                                    <td>{formatDate(item?.updatedAt)}</td>
                                    <td>{item?.offer?.user?.displayName}</td>
                                    <td>
                                        <ImpersonateEye id={getPreferredUser(item.offer).id} />
                                        {getPreferredUser(item.offer).email}
                                    </td>
                                    <td>{item?.offer?.id}</td>
                                    <td>
                                        {item?.socialPlatform ? (
                                            <div className="d-flex align-items-center my-2">
                                                <img
                                                    src={item?.socialPlatform?.path}
                                                    alt={item?.socialPlatform?.name}
                                                />
                                                <div className="d-flex flex-column">
                                                    <span className="font-weight-bolder ml-1">
                                                        {getPlatformValue(item?.socialPlatform?.name)}
                                                    </span>
                                                    <span className="ml-1">
                                                        {!item?.additionalPost
                                                            ? `${item?.event?.platformPosition ?? '-'}/${
                                                                  item?.event?.offerItem?.nbPosts
                                                              }`
                                                            : 'Additional'}
                                                    </span>
                                                </div>
                                            </div>
                                        ) : (
                                            <img
                                                alt=""
                                                src="/images/placeholder.png"
                                                style={{ width: 40, height: 40 }}
                                                className="rounded"
                                            />
                                        )}
                                    </td>
                                    {!isRejected && (
                                        <td>
                                            {item?.additionalPost ? (
                                                item?.isVerified ? (
                                                    <span className="word-break text-primary">
                                                        <FaCheck />
                                                    </span>
                                                ) : (
                                                    <span className="word-break text-danger">
                                                        <FaTimes />
                                                    </span>
                                                )
                                            ) : item?.event?.isVerified ? (
                                                <span className="word-break text-primary">
                                                    <FaCheck />
                                                </span>
                                            ) : (
                                                <span className="word-break text-danger">
                                                    <FaTimes />
                                                </span>
                                            )}
                                        </td>
                                    )}
                                    {isRejected && item?.verifyDate && <td>{formatDate(item?.verifyDate)}</td>}
                                    {isRejected && (
                                        <td>
                                            {item?.rejectReason && (
                                                <textarea
                                                    className="form-control"
                                                    style={{ minHeight: 'min-content' }}
                                                    readOnly
                                                    value={item?.rejectReason}
                                                />
                                            )}
                                        </td>
                                    )}

                                    <td>
                                        <NavLink
                                            className="btn btn-primary"
                                            to={{ pathname: '/reporting/edit/' + item.id }}
                                        >
                                            Edit
                                        </NavLink>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </TableLayout>
        </div>
    );
}
